import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Gamepad2, Coins, Blocks, CircleDot, Trophy } from "lucide-react";
import { toast } from "sonner";
import PongGame from "@/components/games/PongGame";
import BlocksGame from "@/components/games/BlocksGame";

const GAMES = [
  { id: "blocks", name: "Block Drop", desc: "A classic falling-blocks puzzle — clear lines to score.", icon: Blocks },
  { id: "pong", name: "Paddle Pong", desc: "Keep the rally alive against the AI paddle.", icon: CircleDot },
];

const MEDALS = ["🥇", "🥈", "🥉"];

export default function Games() {
  const { user, refresh } = useAuth();
  const [active, setActive] = useState(null);
  const [board, setBoard] = useState({ blocks: [], pong: [] });

  const loadBoard = useCallback(
    () => api.get("/games/leaderboard").then((r) => setBoard(r.data)).catch(() => {}),
    []
  );
  useEffect(() => { loadBoard(); }, [loadBoard]);

  const handleGameOver = async (game, score) => {
    if (!user) {
      toast.info("Sign in to earn AMIR from your score!");
      return;
    }
    if (score <= 0) return;
    try {
      const { data } = await api.post("/games/reward", { game, score });
      if (data.reward > 0) {
        toast.success(`🎉 You earned ${data.reward} AMIR! (${data.daily_remaining} left today)`);
        refresh();
      } else {
        toast.info("Great game! You've hit today's AMIR earning cap — play on for fun 💜");
      }
      loadBoard();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-12">
      <div className="flex items-center gap-3 mb-2">
        <Gamepad2 className="text-[#6A0DAD]" size={30} />
        <h1 className="font-display text-4xl md:text-5xl text-[#1A202C]">Games</h1>
      </div>
      <p className="text-[#4A5568] mb-4 max-w-2xl leading-relaxed">
        Take a breather and play — your scores earn <span className="font-semibold text-[#6A0DAD]">community AMIR points</span>.
        A little joy for the families who need it most. 💜
      </p>
      <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#D4AF37]/12 text-[#B8972E] text-sm font-heading font-semibold mb-8">
        <Coins size={15} /> Earn up to 100 AMIR per day from games
        {user && <span className="text-[#4A5568] font-normal">· balance: {user.amir_balance}</span>}
      </div>

      {!user && (
        <div className="bg-white border border-[#6A0DAD] rounded-2xl p-4 mb-8 text-[#4A5568]">
          You can play freely. <Link to="/auth" className="text-[#6A0DAD] font-semibold">Sign in</Link> to earn AMIR from your scores.
        </div>
      )}

      {!active ? (
        <div className="grid sm:grid-cols-2 gap-4">
          {GAMES.map((game) => (
            <button
              key={game.id}
              data-testid={`game-card-${game.id}`}
              onClick={() => setActive(game.id)}
              className="text-left bg-white border border-[#6A0DAD] rounded-2xl p-7 shadow-sm hover:shadow-md transition-all"
            >
              <span className="grid place-items-center w-12 h-12 rounded-xl bg-[#6A0DAD] text-white mb-4"><game.icon size={22} /></span>
              <h2 className="font-heading text-xl font-semibold text-[#1A202C]">{game.name}</h2>
              <p className="text-[#4A5568] mt-1.5 leading-relaxed">{game.desc}</p>
              <span className="mt-4 inline-block text-[#6A0DAD] font-heading font-semibold text-sm">Play →</span>
            </button>
          ))}
        </div>
      ) : (
        <div>
          <button data-testid="game-back-btn" onClick={() => setActive(null)} className="mb-5 text-[#6A0DAD] font-heading font-semibold">← Back to games</button>
          <div className="bg-white border border-[#6A0DAD] rounded-2xl p-6 shadow-sm">
            {active === "pong" ? (
              <PongGame onGameOver={(s) => handleGameOver("pong", s)} />
            ) : (
              <BlocksGame onGameOver={(s) => handleGameOver("blocks", s)} />
            )}
          </div>
        </div>
      )}

      {/* Leaderboard */}
      <section className="mt-12" data-testid="leaderboard">
        <div className="flex items-center gap-3 mb-6">
          <Trophy className="text-[#D4AF37]" size={26} />
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#1A202C]">Leaderboard</h2>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {GAMES.map((game) => {
            const rows = board[game.id] || [];
            return (
              <div key={game.id} data-testid={`leaderboard-${game.id}`} className="bg-white border border-[#6A0DAD] rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4">
                  <game.icon size={18} className="text-[#6A0DAD]" />
                  <h3 className="font-heading text-lg font-semibold text-[#1A202C]">{game.name}</h3>
                </div>
                {rows.length === 0 ? (
                  <p className="text-[#4A5568] text-sm">No scores yet — be the first on the board!</p>
                ) : (
                  <ol className="space-y-2">
                    {rows.map((r, i) => (
                      <li key={`${r.user_name}-${i}`} className={`flex items-center justify-between px-3 py-2 rounded-xl ${i < 3 ? "bg-[#6A0DAD]/6" : ""}`}>
                        <span className="flex items-center gap-2 font-heading text-[#1A202C]">
                          <span className="w-6 text-center">{MEDALS[i] || <span className="text-[#4A5568] text-sm">{i + 1}</span>}</span>
                          {r.user_name}
                        </span>
                        <span className="font-heading font-bold text-[#6A0DAD]">{r.best_score.toLocaleString()}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}
